import { useEffect, useState } from 'react';
import { Table, Drawer, Select, Button, Input, Textarea, Badge, Alert, Checkbox, useToast } from '@dreamtree-org/twreact-ui';
import { Phone, Mail, MessageCircle, FileText, UserPlus } from 'lucide-react';
import Can from '@rbac/Can';
import { PageHeader, QueryState, EmptyState, StatusBadge, Money, KeyValue, SectionCard } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { rupeesToPaise, paiseToRupees } from '../lib/money';
import { labelOf } from '../lib/catalog';
import { consentedChannels } from '../lib/settings';

const STAGES = ['new', 'contacted', 'qualified', 'quoted', 'won', 'lost'];


const ICON = { call: Phone, whatsapp: MessageCircle, email: Mail };

const SOURCES = ['Walk-in', 'Phone call', 'WhatsApp', 'Referral'];
const EMPTY_LEAD = { name: '', company: '', source: 'Walk-in', interest: '', quantity: '1', budget: '', phone: '', email: '', area: '', consent: { call: false, whatsapp: false, email: false } };

/** A walk-in, call or WhatsApp enquiry recorded by staff (D15). Consent is what the person told you. */
function AddLead({ open, onClose, onAdded }) {
  const add = useMutation('merchantLeadAdd');
  const [f, setF] = useState(EMPTY_LEAD);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    try {
      const res = await add.run({ ...f, quantity: Number(f.quantity), budget_paise: f.budget ? rupeesToPaise(f.budget) : null });
      setF(EMPTY_LEAD);
      onAdded(res.data);
    } catch { /* add.error */ }
  };
  return (
    <Drawer isOpen={open} onClose={onClose} title="Add a lead" description="Record an enquiry from the counter, a call or WhatsApp." size="md">
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} required />
          <Input label="Company (optional)" value={f.company} onChange={(e) => set('company', e.target.value)} />
          <Select label="Source" options={SOURCES.map((x) => ({ value: x, label: x }))} value={f.source} onChange={(v) => set('source', v || 'Walk-in')} closeOnSelect />
          <Input label="Area" value={f.area} onChange={(e) => set('area', e.target.value)} placeholder="e.g. Koramangala" />
          <Input label="Interested in" value={f.interest} onChange={(e) => set('interest', e.target.value)} placeholder="e.g. ThinkPad T14 for the office" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Quantity" type="number" value={f.quantity} onChange={(e) => set('quantity', e.target.value)} />
            <Input label="Budget (₹, total)" value={f.budget} onChange={(e) => set('budget', e.target.value)} />
          </div>
          <Input label="Mobile" type="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          <Input label="Email" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
        </div>
        <fieldset className="space-y-1">
          <legend className="text-sm font-medium">The customer agreed to be contacted by</legend>
          {[['call', 'Phone call'], ['whatsapp', 'WhatsApp'], ['email', 'Email']].map(([k, l]) => (
            <Checkbox key={k} label={l} checked={f.consent[k]} onChange={(e) => set('consent', { ...f.consent, [k]: e.target.checked })} />
          ))}
        </fieldset>
        {add.error ? <Alert variant="error">{add.error}</Alert> : null}
        <Button leftIcon={<UserPlus size={16} />} loading={add.loading} disabled={!f.name.trim()} onClick={submit}>Add lead</Button>
      </div>
    </Drawer>
  );
}

/** SELL-06 — lead inbox: status pipeline, consent-scoped contact, convert to quotation (real since D7). */
export default function Customers() {
  const { toast } = useToast();
  const [tab, setTab] = useState('all');
  // "overdue" is a filter across stages (open leads past their follow-up date), filtered on the server.
  const leads = useQuery('merchantLeads', tab === 'overdue' ? { status: 'all', overdue: true } : { status: tab });
  const follow = useMutation('merchantLeadFollowUp');
  const [ff, setFf] = useState({ next_action: '', follow_up_at: '' });
  const update = useMutation('merchantLeadUpdate');
  const quote = useMutation('merchantLeadQuote');
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  const [qf, setQf] = useState({ price: '', quantity: '1', valid_days: '7', note: '' });

  useEffect(() => {
    if (open) setFf({ next_action: open.next_action || '', follow_up_at: open.follow_up_at ? String(open.follow_up_at).slice(0, 10) : '' });
  }, [open]);

  useEffect(() => {
    if (open) setQf({ price: open.budget_paise ? paiseToRupees(Math.round(open.budget_paise / Math.max(1, open.quantity))) : '', quantity: String(open.quantity), valid_days: '7', note: '' });
  }, [open && open.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = (leads.data && leads.data.counts) || {};
  const refresh = (lead) => {
    setOpen(lead);
    leads.reload();
  };

  const changeStatus = async (status) => {
    try {
      const res = await update.run({ id: open.id, status });
      toast.success('Lead updated', `${open.name} is now ${status}.`);
      refresh(res.data);
    } catch { /* update.error shown */ }
  };
  const saveFollowUp = async () => {
    try {
      const res = await follow.run({ id: open.id, next_action: ff.next_action || null, follow_up_at: ff.follow_up_at || null });
      toast.success('Follow-up saved', ff.follow_up_at ? `Due ${formatDate(ff.follow_up_at)}` : open.name);
      refresh(res.data);
    } catch { /* follow.error shown */ }
  };
  const makeQuote = async () => {
    const unit = rupeesToPaise(qf.price);
    try {
      const res = await quote.run({ id: open.id, unit_price_paise: unit, quantity: Number(qf.quantity), valid_days: Number(qf.valid_days), note: qf.note });
      toast.success('Quotation created', `${res.data.quotation.number}${res.data.mailed ? ' · emailed to the customer' : ''}`);
      refresh(res.data);
    } catch { /* quote.error shown */ }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Customers & leads"
        subtitle="Enquiries from your storefront, WhatsApp and walk-ins. Contact details are shown only where the customer consented."
        actions={<Can module="sales" action="create"><Button leftIcon={<UserPlus size={16} />} onClick={() => setAdding(true)}>Add lead</Button></Can>}
      />
      <AddLead open={adding} onClose={() => setAdding(false)} onAdded={(lead) => { setAdding(false); toast.success('Lead added', lead.id); refresh(lead); }} />
      {/* Phones: one swipeable row of stage chips; from sm a seven-column strip. */}
      <nav aria-label="Lead pipeline" className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-8 sm:overflow-visible sm:px-0 sm:pb-0">
        {['all', ...STAGES, 'overdue'].map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={tab === s}
            onClick={() => setTab(s)}
            className={`min-w-24 shrink-0 snap-start rounded-md border p-2 text-left min-h-[44px] sm:min-w-0 ${tab === s ? 'border-primary-500 bg-primary-50 dark:bg-primary-950' : 'border-[var(--app-border)] bg-[var(--app-surface)]'}`}
          >
            <div className={`text-xs capitalize ${s === 'overdue' ? 'text-error-600' : 'opacity-70'}`}>{s === 'overdue' ? 'Overdue follow-ups' : s}</div>
            <div className="text-lg font-bold">{counts[s] ?? '…'}</div>
          </button>
        ))}
      </nav>
      <QueryState query={leads}>
        {(res) =>
          res.data.length ? (
            <Table
              data={res.data}
              columns={[
                { key: 'name', label: 'Lead', render: (l) => <div><div className="font-medium">{l.name}</div>{l.company ? <div className="text-xs opacity-70">{l.company}</div> : null}</div> },
                { key: 'interest', label: 'Interested in', render: (l) => `${l.interest}${l.quantity > 1 ? ` × ${l.quantity}` : ''}` },
                { key: 'budget_paise', label: 'Budget', render: (l) => <Money paise={l.budget_paise} />, hideOnMobile: true },
                { key: 'source', label: 'Source', hideOnMobile: true },
                { key: 'next_action', label: 'Next action', wrap: true, render: (l) => l.next_action || <span className="opacity-60">—</span>, hideOnMobile: true },
                {
                  key: 'follow_up_at', label: 'Follow up',
                  render: (l) => (l.follow_up_at ? (
                    <span className="inline-flex flex-wrap items-center gap-1">{formatDate(l.follow_up_at)}{l.overdue ? <Badge variant="error" size="sm">Overdue</Badge> : null}</span>
                  ) : <span className="opacity-60">—</span>),
                },
                {
                  key: 'contact', label: 'Contact',
                  render: (l) => {
                    const ch = consentedChannels(l);
                    return ch.length ? <span className="inline-flex gap-1">{ch.map((c) => <Badge key={c.key} variant="outline" size="sm">{c.label}</Badge>)}</span> : <Badge variant="secondary" size="sm">No consent</Badge>;
                  },
                },
                { key: 'status', label: 'Status', render: (l) => <StatusBadge status={l.status} /> },
                { key: 'created_at', label: 'Received', render: (l) => formatDate(l.created_at), hideOnMobile: true },
              ]}
              onRowClick={(l) => setOpen(l)}
              showSerial={false}
              withAction={false}
              showColumnPicker={false}
              pagination
              pageSize={25}
              onReload={leads.reload}
            />
          ) : (
            <EmptyState title={tab === 'overdue' ? 'No overdue follow-ups' : 'No leads here'}>Leads land here when shoppers use “Request a quote” on your storefront, or when you add one.</EmptyState>
          )
        }
      </QueryState>

      <Drawer isOpen={!!open} onClose={() => setOpen(null)} title={open ? open.name : ''} description={open ? `${open.source} · ${formatDate(open.created_at)}` : ''} size="md">
        {open ? (
          <div className="space-y-4">
            <KeyValue items={[
              { label: 'Company', value: open.company },
              { label: 'Interested in', value: `${open.interest} × ${open.quantity}` },
              { label: 'Budget', value: <Money paise={open.budget_paise} /> },
              { label: 'Area', value: open.area || '—' },
              open.message ? { label: 'Message', value: open.message } : null,
            ]} />
            <SectionCard title="Contact (consent-scoped)">
              {consentedChannels(open).length ? (
                <ul className="space-y-2">
                  {consentedChannels(open).map((c) => {
                    const I = ICON[c.key];
                    return (
                      <li key={c.key}>
                        <a href={c.href} className="inline-flex items-center gap-2 min-h-[44px] hover:underline" target={c.key === 'whatsapp' ? '_blank' : undefined} rel="noreferrer">
                          <I size={16} /> {c.label}: <span className="font-medium">{c.value}</span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <Alert variant="warning">The customer has not consented to be contacted. Reply only through the storefront enquiry.</Alert>
              )}
              <div className="flex flex-wrap gap-1 text-xs">
                {['call', 'whatsapp', 'email'].map((k) => <Badge key={k} variant={open.consent[k] ? 'success' : 'secondary'} size="sm">{k}: {open.consent[k] ? 'yes' : 'no'}</Badge>)}
              </div>
            </SectionCard>

            <SectionCard title="Follow-up">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Next action" value={ff.next_action} onChange={(e) => setFf({ ...ff, next_action: e.target.value })} placeholder="e.g. Send ThinkPad options" maxLength={255} />
                <Input label="Follow up on" type="date" value={ff.follow_up_at} onChange={(e) => setFf({ ...ff, follow_up_at: e.target.value })} />
              </div>
              {open.overdue ? <Alert variant="warning">This follow-up is overdue.</Alert> : null}
              {follow.error ? <Alert variant="error">{follow.error}</Alert> : null}
              <Can module="sales" action="update">
                <Button size="sm" variant="outline" loading={follow.loading} onClick={saveFollowUp}>Save follow-up</Button>
              </Can>
            </SectionCard>

            <Select label="Pipeline stage" options={STAGES.map((s) => ({ value: s, label: labelOf(s) }))} value={open.status} onChange={changeStatus} closeOnSelect />
            {update.error ? <Alert variant="error">{update.error}</Alert> : null}

            <SectionCard title={open.quotation ? `Quotation ${open.quotation.number}` : 'Convert to quotation'}>
              {open.quotation ? (
                <KeyValue items={[
                  { label: 'Unit price', value: <Money paise={open.quotation.unit_price_paise} /> },
                  { label: 'Quantity', value: open.quotation.quantity },
                  { label: 'Total', value: <Money paise={open.quotation.total_paise} className="font-bold" /> },
                  { label: 'Valid until', value: formatDate(open.quotation.valid_until) },
                ]} />
              ) : (
                <div className="space-y-3">
                  <div className="grid gap-3 grid-cols-2">
                    <Input label="Unit price (₹)" value={qf.price} onChange={(e) => setQf({ ...qf, price: e.target.value })} error={qf.price && rupeesToPaise(qf.price) == null ? 'Enter rupees' : undefined} />
                    <Input label="Quantity" type="number" value={qf.quantity} onChange={(e) => setQf({ ...qf, quantity: e.target.value })} />
                    <Input label="Valid for (days)" type="number" value={qf.valid_days} onChange={(e) => setQf({ ...qf, valid_days: e.target.value })} />
                  </div>
                  <Textarea label="Note to customer" rows={2} value={qf.note} onChange={(e) => setQf({ ...qf, note: e.target.value })} />
                  {quote.error ? <Alert variant="error">{quote.error}</Alert> : null}
                  <Button leftIcon={<FileText size={16} />} loading={quote.loading} disabled={!rupeesToPaise(qf.price) || !(Number(qf.quantity) >= 1)} onClick={makeQuote}>
                    Create quotation
                  </Button>
                </div>
              )}
            </SectionCard>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}
